import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy, OnInit, computed, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

import {
    ApiStepperItem,
    MERCHANT_ID_QUERY_PARAM,
    STEP_LABELS_AOW,
    STEP_ORDER_AOW,
    STEP_PATH_AOW,
    StepKeyAOW,
    StepStateAOW,
    StepStatus,
} from '../../model/client-onboarding/acquire-onboarding-wizard-stepper';

import { AowHeaderComponent } from './aow-header/aow-header.component';
import { UtilitiesService } from '../../services/utilities.service';
import { normalizeFromApi } from '../../model/client-onboarding/acquire-onboarding-wizard-stepper-helper';
import { AcquireMerchant, AcquireOnboardingWizardService } from '../../services/acquire-onboarding-wizard/acquire-onboarding-wizard.service';
import { distinctUntilChanged, filter, forkJoin, map, startWith, Subject, switchMap, take, takeUntil } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';

@Component({
    selector: 'app-acquire-onboarding-wizard',
    standalone: true,
    imports: [
        CommonModule,
        RouterOutlet,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
        AowHeaderComponent,
        MatCardModule,
        MatSelectModule,
        MatOptionModule,
        ReactiveFormsModule,
    ],
    templateUrl: './acquire-onboarding-wizard.component.html',
    styleUrl: './acquire-onboarding-wizard.component.scss',
})
export class AcquireOnboardingWizardComponent implements OnInit, OnDestroy {

    private STORAGE_KEY = 'fd_acquire_onboarding_sidebar_collapsed';

    collapsed = signal<boolean>(this.readCollapsed());
    labels = signal(STEP_LABELS_AOW);
    steps = signal<StepStateAOW[]>([]);
    onboardingId!: number;
    clientId!: number;
    showDemoAlert = signal(true);
    merchants: AcquireMerchant[] = [];
    merchantControl = new FormControl<number>(null);

    private destroy$ = new Subject<void>();
    private firstNavDone = false;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        public utilities: UtilitiesService,
        private acquireOnboardingService: AcquireOnboardingWizardService,
        private cdr: ChangeDetectorRef,
        private fb: FormBuilder
    ) {
        this.merchantControl = this.fb.control<number>(null);
        this.utilities.updateRouteOnboardingStatus(true);
        this.utilities.fullPageLoading = true;
    }

    ngOnInit(): void {
        this.utilities.showLogo = true;

        const id$ = this.route.paramMap.pipe(
            map(pm => Number(pm.get('onboardingId'))),
            filter(id => !Number.isNaN(id) && id > 0),
            distinctUntilChanged()
        );

        id$.pipe(takeUntil(this.destroy$)).subscribe(id => {
            this.onboardingId = id;
            this.clientId = Number(this.route.snapshot.paramMap.get('clientId'));
            this.firstNavDone = false;
            this.steps.set([]);
            this.utilities.fullPageLoading = true;
        });

        this.route.queryParamMap.pipe(
            map(pm => this.parseMerchantId(pm.get(MERCHANT_ID_QUERY_PARAM))),
            distinctUntilChanged(),
            takeUntil(this.destroy$),
        ).subscribe(id => {
            if (id != null) {
                this.utilities.setMerchantOnboardingId(id);
                this.merchantControl.setValue(id, { emitEvent: false });
            }
        });

        const loadWizardData$ = (id: number) => forkJoin({
            steps: this.acquireOnboardingService.refresh(id).pipe(take(1)),
            merchants: this.acquireOnboardingService.getMerchants(id).pipe(take(1)),
        });

        id$.pipe(
            switchMap(id => loadWizardData$(id)),
            takeUntil(this.destroy$),
        ).subscribe({
            next: ({ steps, merchants }) => this.applyWizardData(steps, merchants, true),
            error: (error) => console.error('Error loading wizard data', error),
        });

        id$.pipe(
            switchMap(id =>
                this.router.events.pipe(
                    filter((e): e is NavigationEnd => e instanceof NavigationEnd),
                    switchMap(() => loadWizardData$(id)),
                )
            ),
            takeUntil(this.destroy$),
        ).subscribe({
            next: ({ steps, merchants }) => this.applyWizardData(steps, merchants, false),
            error: (error) => console.error('Error refreshing wizard data', error),
        });
    }

    private applyWizardData(
        stepItems: ApiStepperItem[],
        merchants: AcquireMerchant[],
        isInitial: boolean,
    ): void {
        const mapped = normalizeFromApi(stepItems);
        this.merchants = merchants;
        this.steps.set(mapped);

        if (this.merchants.length === 1 && this.merchantIdFromQuery() == null) {
            const singleId = Number(this.merchants[0].id);
            this.applyMerchantId(singleId, false);
        }

        if (isInitial) {
            this.utilities.fullPageLoading = false;
            this.firstNavDone = true;
        }

        if (this.shouldAutoNavigateToActiveStep(mapped)) {
            this.navigateToActive(mapped);
        }
        this.cdr.markForCheck();
    }

    setMerchantOnboardingId(id: number | string): void {
        const numId = Number(id);
        if (Number.isNaN(numId) || numId <= 0) return;
        this.applyMerchantId(numId, true);
    }

    private applyMerchantId(id: number, updateUrl: boolean): void {
        this.utilities.setMerchantOnboardingId(id);
        this.merchantControl.setValue(id, { emitEvent: false });
        if (updateUrl) {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { [MERCHANT_ID_QUERY_PARAM]: id },
                queryParamsHandling: 'merge',
            });
        }
    }

    redirectSelectClient(): void {
        this.router.navigate(['select-client']);
    }

    private merchantIdFromQuery(): number | null {
        return this.parseMerchantId(this.route.snapshot.queryParamMap.get(MERCHANT_ID_QUERY_PARAM));
    }

    private parseMerchantId(raw: string | null): number | null {
        if (raw == null || raw === '') return null;
        const id = Number(raw);
        return !Number.isNaN(id) && id > 0 ? id : null;
    }

    private merchantQueryParams(): { merchantId: number } | null {
        const id = this.merchantControl.value ?? this.utilities.merchantOnboardingIdValue ?? this.merchantIdFromQuery();
        return id != null ? { [MERCHANT_ID_QUERY_PARAM]: id } : null;
    }

    toggleCollapsed() {
        this.collapsed.update(v => !v);
        try { localStorage.setItem(this.STORAGE_KEY, String(this.collapsed())); } catch { }
    }

    private readCollapsed(): boolean {
        try { return localStorage.getItem(this.STORAGE_KEY) === 'true'; } catch { return false; }
    }

    statusText: Record<StepStatus, string> = {
        pending: 'Pending',
        inProgress: 'In progress',
        completed: 'Completed',
    };

    statusClass(st: StepStatus) {
        return ({
            pending: 'chip--pending',
            inProgress: 'chip--inprogress',
            completed: 'chip--completed',
        } as Record<string, string>)[st];
    }

    isCompleted(st: StepStatus) { return st === 'completed'; }

    readonly routerUrl = toSignal(
        this.router.events.pipe(
            filter((e): e is NavigationEnd => e instanceof NavigationEnd),
            map(() => this.router.url),
            startWith(this.router.url)
        ),
        { initialValue: this.router.url }
    );

    isOnDecisionPage = computed(() => this.routerUrl().includes('/decision'));

    activeKey = computed<StepKeyAOW>(() => {
        const url = this.routerUrl();
        const hits = Object.entries(STEP_PATH_AOW)
            .filter(([, p]) => url.includes('/' + p))
            .map(([k]) => k as StepKeyAOW);

        if (hits.length === 0) return 'merchantInformation';
        if (hits.includes('merchantInformation')) return 'merchantInformation';
        return hits[hits.length - 1]!;
    });

    selectedIndex = computed(() => {
        const idx = STEP_ORDER_AOW.indexOf(this.activeKey());
        return Math.max(0, idx);
    });

    goTo(key: StepKeyAOW) {
        const path = STEP_PATH_AOW[key];
        const queryParams = this.merchantQueryParams();
        this.router.navigate([path], {
            relativeTo: this.route,
            replaceUrl: !this.firstNavDone,
            ...(queryParams ? { queryParams, queryParamsHandling: 'merge' as const } : {}),
        });
    }

  /*   private hasStepInUrl(url: string): boolean {
        return Object.values(STEP_PATH_AOW).some(p => url.includes('/' + p));
    } */

    private shouldAutoNavigateToActiveStep(arr: StepStateAOW[]): boolean {
        if (!arr?.length) return false;
        const merchantId = this.merchantIdFromQuery();
        if (merchantId != null) {
            return this.isOnDecisionPage();
        }
        return false;
    }

    private navigateToActive(arr: StepStateAOW[]) {
        if (!arr?.length) return;
        const existing = STEP_ORDER_AOW.filter(k => arr.some(s => s.step === k));

        const allCompleted =
            existing.length > 0 &&
            existing.every(k => arr.find(s => s.step === k)?.status === 'completed');

        if (allCompleted) {
            const last = existing[existing.length - 1] as StepKeyAOW;
            return this.goTo(last);
        }

        const inProg = existing.find(k =>
            arr.some(s => s.step === k && s.status === 'inProgress')
        );
        if (inProg) return this.goTo(inProg);

        const pending = existing.find(k =>
            arr.some(s => s.step === k && s.status === 'pending')
        );
        if (pending) return this.goTo(pending);

        return this.goTo(existing[0] as StepKeyAOW);
    }

    hideDemoAlert(): void {
        this.showDemoAlert.set(false);
    }


    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
